import { cn } from "@/lib/utils";

export interface SectionHeadingProps {
  eyebrow?: string;
  title: string;
  description?: string;
  align?: "left" | "center";
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
}

/**
 * SectionHeading: lightweight heading used across marketing sections.
 * - Eyebrow is optional and restrained (text-[11px] uppercase tracking).
 * - Title uses tight tracking, balance-wrapped.
 * - Description capped at prose width.
 *
 * Page composition rule: max 1 eyebrow per 3 sections. The hero counts as one.
 */
export function SectionHeading({
  eyebrow,
  title,
  description,
  align = "center",
  className,
  titleClassName,
  descriptionClassName,
}: SectionHeadingProps) {
  return (
    <div
      className={cn(
        "max-w-3xl",
        align === "center" && "mx-auto text-center",
        align === "left" && "text-left",
        className,
      )}
    >
      {eyebrow && (
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
          {eyebrow}
        </p>
      )}
      <h2
        className={cn(
          "text-balance font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl md:text-[2.75rem] md:leading-[1.1]",
          titleClassName,
        )}
      >
        {title}
      </h2>
      {description && (
        <p
          className={cn(
            "mt-4 text-base text-muted-foreground leading-relaxed sm:text-[17px]",
            descriptionClassName,
          )}
        >
          {description}
        </p>
      )}
    </div>
  );
}
